// Interactions that drive Lottie animations:
//  - HowItWorks: home page "How It Works [In View]" (a looping three-step walkthrough).
//  - LottieRestartOnEnter: the DBOS Conductor feature grid, whose animations restart when the
//    grid's top crosses the middle of the viewport while scrolling down.

import type { AnimationItem } from 'lottie-web';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { WfProps } from '../webflow/element';
import { LottieRegistryContext } from '../webflow/Lottie';
import { css, ease, type EasingName } from './easing';
import { useInView } from './useInView';

/** Tracks the Lottie animations rendered inside a component. */
function useLottieRegistry() {
  const [anims] = useState(() => new Map<HTMLElement, AnimationItem>());
  const register = useCallback(
    (el: HTMLElement, anim: AnimationItem) => {
      anims.set(el, anim);
      return () => void anims.delete(el);
    },
    [anims],
  );
  return { anims, register };
}

// --- How It Works ------------------------------------------------------------------------------

const BORDER_ACTIVE = 'rgb(184, 204, 216)';
const BORDER_IDLE = 'rgb(60, 75, 98)';

export function HowItWorks({ as: Tag = 'div', children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  const { anims, register } = useLottieRegistry();
  const inView = useInView(ref);

  useEffect(() => {
    const root = ref.current;
    if (!inView || !root) return undefined;
    const steps = [...root.querySelectorAll<HTMLElement>('.layout-2col_item.is-how')].map((el) => ({
      el,
      bg: el.querySelector<HTMLElement>('.product-visual-bg'),
      visual: el.querySelector<HTMLElement>('.product-visual-lottie'),
      ring: el.querySelector<HTMLElement>('.loader-ring'),
    }));
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const frames = new Set<number>();
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));

    // Plays a Lottie from its first to its last frame over `duration`, like IX2's Lottie action.
    const scrub = (el: HTMLElement | null, duration: number, easing: EasingName) => {
      const anim = el && anims.get(el);
      if (!anim) return;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        anim.goToAndStop(ease(easing, t) * (anim.totalFrames - 1), true);
        if (t < 1) frames.add(requestAnimationFrame(tick));
      };
      frames.add(requestAnimationFrame(tick));
    };
    const reset = (el: HTMLElement | null) => el && anims.get(el)?.goToAndStop(0, true);
    const fade = (el: HTMLElement | null, opacity: number) => {
      if (!el) return;
      el.style.transition = 'opacity 500ms linear';
      el.style.opacity = String(opacity);
    };
    const highlight = (el: HTMLElement, on: boolean) => {
      el.style.transition = `transform 200ms ${css('easeInOut')}, border-color 200ms ${css('ease')}`;
      el.style.transform = on ? 'scale3d(1.02, 1.02, 1)' : 'scale3d(1, 1, 1)';
      el.style.borderColor = on ? BORDER_ACTIVE : BORDER_IDLE;
    };

    const cycle = () => {
      const [a, b, c] = steps;
      if (!a || !b || !c) return;
      // Step 1 (0s): its animations start after a 1s pause.
      highlight(a.el, true);
      fade(a.bg, 0);
      at(1000, () => {
        scrub(a.visual, 6000, 'easeInOut');
        scrub(a.ring, 6000, 'linear');
      });
      // Step 2 (7s).
      at(7000, () => {
        highlight(a.el, false);
        highlight(b.el, true);
        scrub(b.visual, 6000, 'easeInOut');
        scrub(b.ring, 6000, 'linear');
        reset(a.ring);
        fade(b.bg, 0);
        fade(a.bg, 1);
      });
      // Step 3 (13s).
      at(13000, () => {
        highlight(b.el, false);
        highlight(c.el, true);
        scrub(c.visual, 6000, 'easeInOut');
        scrub(c.ring, 6000, 'linear');
        reset(b.ring);
        fade(c.bg, 0);
        fade(b.bg, 1);
      });
      // Reset (19s), then loop.
      at(19000, () => {
        highlight(c.el, false);
        steps.forEach((s) => reset(s.visual));
        reset(c.ring);
        fade(c.bg, 1);
      });
      at(19500, cycle);
    };
    cycle();
    return () => {
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [inView, anims]);

  return (
    <LottieRegistryContext.Provider value={register}>
      <Tag ref={ref} {...props}>
        {children}
      </Tag>
    </LottieRegistryContext.Provider>
  );
}

// --- Restart on scroll -------------------------------------------------------------------------

/**
 * Restarts the Lottie animations at `indices` (in document order) each time the element's top
 * scrolls past the middle of the viewport (GSAP ScrollTrigger start "top 50%", end "bottom top",
 * toggle action "restart" on enter only).
 */
export function LottieRestartOnEnter({
  as: Tag = 'div',
  indices,
  children,
  ...props
}: WfProps & { indices: number[] }) {
  const ref = useRef<HTMLElement>(null);
  const { anims, register } = useLottieRegistry();

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    type Zone = 'before' | 'active' | 'after';
    const zone = (): Zone => {
      const r = root.getBoundingClientRect();
      if (r.top > window.innerHeight / 2) return 'before';
      return r.bottom > 0 ? 'active' : 'after';
    };
    let last = zone();
    const onScroll = () => {
      const now = zone();
      if (last === 'before' && now !== 'before') {
        const lotties = [...root.querySelectorAll<HTMLElement>('[data-animation-type="lottie"]')];
        for (const i of indices) {
          const el = lotties[i];
          const anim = el && anims.get(el);
          anim?.goToAndPlay(0, true);
        }
      }
      last = now;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [anims, indices]);

  return (
    <LottieRegistryContext.Provider value={register}>
      <Tag ref={ref} {...props}>
        {children}
      </Tag>
    </LottieRegistryContext.Provider>
  );
}
